import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { app } from '../firebase';

const LAST_ACTIVE_STORAGE_PREFIX = 'nda-admin-last-active';
const DEFAULT_ACTIVITY_THROTTLE_MS = 60 * 1000;

const getLastActiveStorageKey = (userId) => `${LAST_ACTIVE_STORAGE_PREFIX}:${userId}`;

export const findAdminDocRef = async ({ userId, email, db = getFirestore(app) }) => {
  if (!userId && !email) {
    return null;
  }

  if (userId) {
    const directRef = doc(db, 'Admins', userId);
    const directSnap = await getDoc(directRef);
    if (directSnap.exists()) {
      return directRef;
    }

    const byUserId = await getDocs(query(collection(db, 'Admins'), where('userID', '==', userId)));
    if (!byUserId.empty) {
      return byUserId.docs[0].ref;
    }
  }

  if (email) {
    const byEmail = await getDocs(query(collection(db, 'Admins'), where('userEmail', '==', email)));
    if (!byEmail.empty) {
      return byEmail.docs[0].ref;
    }
  }

  return null;
};

const shouldThrottleActivityUpdate = (userId, minIntervalMs) => {
  if (typeof window === 'undefined' || !userId || minIntervalMs <= 0) {
    return false;
  }

  const lastTouchedAt = Number(window.localStorage.getItem(getLastActiveStorageKey(userId)) || '0');
  return Date.now() - lastTouchedAt < minIntervalMs;
};

const markActivityUpdate = (userId) => {
  if (typeof window === 'undefined' || !userId) {
    return;
  }

  window.localStorage.setItem(getLastActiveStorageKey(userId), String(Date.now()));
};

export const recordAdminLogin = async ({ userId, email, path = '/dashboard' }) => {
  const db = getFirestore(app);
  const adminDocRef = await findAdminDocRef({ userId, email, db });

  if (!adminDocRef) {
    return false;
  }

  await setDoc(adminDocRef, {
    lastLoginAt: serverTimestamp(),
    lastActiveAt: serverTimestamp(),
    lastActivePath: path,
    lastPresenceSource: 'login',
    updatedAt: serverTimestamp(),
  }, { merge: true });

  markActivityUpdate(userId);
  return true;
};

export const recordAdminActivity = async ({
  userId,
  email,
  path = '',
  label = '',
  source = 'activity',
  force = false,
  minIntervalMs = DEFAULT_ACTIVITY_THROTTLE_MS,
}) => {
  if (!userId) {
    return false;
  }

  if (!force && shouldThrottleActivityUpdate(userId, minIntervalMs)) {
    return false;
  }

  const db = getFirestore(app);
  const adminDocRef = await findAdminDocRef({ userId, email, db });

  if (!adminDocRef) {
    return false;
  }

  await setDoc(adminDocRef, {
    lastActiveAt: serverTimestamp(),
    lastActivePath: path,
    lastActiveLabel: label,
    lastPresenceSource: source,
    updatedAt: serverTimestamp(),
  }, { merge: true });

  markActivityUpdate(userId);
  return true;
};
